import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  RefreshCw,
  MapPin,
  Clock,
  Car,
  CheckCircle,
  AlertCircle,
  Trash2,
  LogOut,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { set } from "date-fns";

const TodaysBookingStatus = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const navigate = useNavigate();

  // Status color mapping
  const statusColors = {
    reserved: {
      bg: "bg-blue-50",
      badge: "bg-blue-200 text-blue-900",
      icon: AlertCircle,
      label: "Reserved",
    },
    checked_in: {
      bg: "bg-green-50",
      badge: "bg-green-200 text-green-900",
      icon: CheckCircle,
      label: "Checked In",
    },
    completed: {
      bg: "bg-gray-50",
      badge: "bg-gray-200 text-gray-900",
      icon: CheckCircle,
      label: "Completed",
    },
    cancelled: {
      bg: "bg-red-50",
      badge: "bg-red-200 text-red-900",
      icon: AlertCircle,
      label: "Cancelled",
    },
  };

  // Slot type color mapping
  const slotTypeConfig = {
    Car: {
      color: "bg-gray-100 border-gray-400",
      textColor: "text-black",
      iconColor: "text-black",
      icon: Car,
    },
    Bike: {
      color: "bg-yellow-100 border-yellow-400",
      textColor: "text-black",
      iconColor: "text-black",
      icon: Car,
    },
    electric: {
      color: "bg-green-100 border-green-400",
      textColor: "text-black",
      iconColor: "text-black",
      icon: Car,
    },
    handicapped: {
      color: "bg-blue-100 border-blue-400",
      textColor: "text-black",
      iconColor: "text-black",
      icon: Car,
    },
  };

  const getSlotTypeConfig = (type) => {
    return (
      slotTypeConfig[type] || {
        color: "bg-red-100 border-red-400",
        textColor: "text-black",
        iconColor: "text-black",
      }
    );
  };

  const fetchBookings = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/bookings/today`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Filter for today's bookings
      const today = new Date().toISOString().split("T")[0];
      const todaysBookings = response.data
      setBookings(todaysBookings);
      setLoading(false);
    } catch (error) {
      console.error("Failed to load bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBookings();

    // Auto-refresh every 30 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchBookings, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const handleCheckIn = async (bookingId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/parking/checkin`,
        { booking_id: bookingId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Checked in successfully!");
      fetchBookings();
      setShowDetailsDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-in failed");
    }
  };

  const handleCheckOut = async (bookingId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/parking/checkout/${bookingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Checked out successfully!");
      fetchBookings();
      setShowDetailsDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-out failed");
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?"))
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/parking/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Booking cancelled");
      fetchBookings();
      setShowDetailsDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cancellation failed");
    }
  };

  const getStatusIcon = (status) => {
    const config = statusColors[status] || statusColors.reserved;
    const IconComponent = config.icon;
    return <IconComponent size={16} className="mr-2" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading bookings...</p>
      </div>
    );
  }

  // Calculate statistics
  const stats = {
    total: bookings.length,
    reserved: bookings.filter((b) => b.status === "reserved").length,
    checkedIn: bookings.filter((b) => b.status === "checked_in").length,
    completed: bookings.filter((b) => b.status === "completed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="pt-12 pb-12 px-6 max-w-7xl mx-auto">
        <Button
          onClick={() => navigate("/admin")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </Button>

        <div className="fade-in">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
                Today's Booking Status
              </h1>
              <p className="text-xl text-gray-600">
                Live tracking of your parking bookings for{" "}
                {new Date().toLocaleDateString()}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setAutoRefresh(!autoRefresh)}
                variant={autoRefresh ? "default" : "outline"}
                className={`rounded-full ${
                  autoRefresh
                    ? "bg-[#1A3C34] hover:bg-[#142E28]"
                    : "border-[#1A3C34] text-[#1A3C34]"
                }`}
              >
                <RefreshCw
                  size={18}
                  className={autoRefresh ? "animate-spin" : ""}
                />
                <span className="ml-2">
                  {autoRefresh ? "Auto-Refresh On" : "Auto-Refresh Off"}
                </span>
              </Button>
              <Button
                onClick={fetchBookings}
                disabled={refreshing}
                className="bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-full"
              >
                <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
                <span className="ml-2">Refresh</span>
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <Card className="p-4 bg-[#1A3C34] text-white border-none">
              <p className="text-sm opacity-90 mb-1">Total Bookings</p>
              <p className="text-3xl font-bold">{stats.total}</p>
            </Card>
            <Card className="p-4 bg-blue-100 border-none">
              <p className="text-sm text-blue-900 opacity-90 mb-1">Reserved</p>
              <p className="text-3xl font-bold text-blue-900">{stats.reserved}</p>
            </Card>
            <Card className="p-4 bg-green-100 border-none">
              <p className="text-sm text-green-900 opacity-90 mb-1">Checked In</p>
              <p className="text-3xl font-bold text-green-900">
                {stats.checkedIn}
              </p>
            </Card>
            <Card className="p-4 bg-gray-100 border-none">
              <p className="text-sm text-gray-900 opacity-90 mb-1">Completed</p>
              <p className="text-3xl font-bold text-gray-900">{stats.completed}</p>
            </Card>
            <Card className="p-4 bg-red-100 border-none">
              <p className="text-sm text-red-900 opacity-90 mb-1">Cancelled</p>
              <p className="text-3xl font-bold text-red-900">{stats.cancelled}</p>
            </Card>
          </div>

          {/* Bookings List */}
          {bookings.length === 0 ? (
            <Card className="p-12 text-center">
              <MapPin className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600 text-lg mb-4">
                No bookings for today
              </p>
              <Button
                onClick={() => navigate("/parking")}
                className="bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full px-6"
              >
                Book a Parking Slot
              </Button>
            </Card>
          ) : (
            <Card className="border border-gray-100">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-bold text-[#1A3C34]">
                        Slot
                      </TableHead>
                      <TableHead className="font-bold text-[#1A3C34]">
                        Type
                      </TableHead>
                      <TableHead className="font-bold text-[#1A3C34]">
                        Time
                      </TableHead>
                      <TableHead className="font-bold text-[#1A3C34]">
                        Vehicle
                      </TableHead>
                      <TableHead className="font-bold text-[#1A3C34]">
                        Floor
                      </TableHead>
                      <TableHead className="font-bold text-[#1A3C34]">
                        Status
                      </TableHead>
                      <TableHead className="font-bold text-[#1A3C34]">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((booking) => {
                      const statusConfig = statusColors[booking.status];
                      const slotTypeConfig = getSlotTypeConfig(
                        booking.slot_type
                      );
                      return (
                        <TableRow
                          key={booking._id}
                          className={`${statusConfig?.bg || "bg-white"} hover:bg-gray-100 cursor-pointer`}
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowDetailsDialog(true);
                          }}
                        >
                          <TableCell className="font-bold text-lg">
                            {booking.slot_number}
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={`${slotTypeConfig.color} ${slotTypeConfig.textColor} border`}
                            >
                              {booking.slot_type || "Unknown"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center text-sm">
                              <Clock size={14} className="mr-2" />
                              {booking.time_slot?.name}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {booking.vehicle?.make} {booking.vehicle?.model}- {booking.vehicle?.license_plate}
                          </TableCell>
                          <TableCell className="text-sm">
                            {booking.floor}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              {getStatusIcon(booking.status)}
                              <Badge className={statusConfig?.badge}>
                                {statusConfig?.label}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {booking.status === "reserved" && (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCheckIn(booking._id);
                                  }}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                                >
                                  <CheckCircle size={16} className="mr-1" />
                                  Check In
                                </Button>
                              )}
                              {booking.status === "checked_in" && (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCheckOut(booking._id);
                                  }}
                                  size="sm"
                                  className="bg-orange-600 hover:bg-orange-700 text-white rounded-full"
                                >
                                  <LogOut size={16} className="mr-1" />
                                  Check Out
                                </Button>
                              )}
                              {(booking.status === "reserved" ||
                                booking.status === "checked_in") && (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancel(booking._id);
                                  }}
                                  size="sm"
                                  variant="destructive"
                                  className="rounded-full"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}

          {/* Booking Details Dialog */}
          <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#1A3C34]">
                  Booking Details
                </DialogTitle>
              </DialogHeader>
              {selectedBooking && (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Slot Number</p>
                    <p className="text-2xl font-bold text-[#1A3C34]">
                      {selectedBooking.slot_number}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Slot Type</p>
                      <Badge className={getSlotTypeConfig(selectedBooking.slot_type).color + " " + getSlotTypeConfig(selectedBooking.slot_type).textColor + " border"}>
                        {selectedBooking.slot_type || "Unknown"}
                      </Badge>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Status</p>
                      <Badge
                        className={statusColors[selectedBooking.status]?.badge}
                      >
                        {statusColors[selectedBooking.status]?.label}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Date</p>
                      <p className="font-semibold">{selectedBooking.date}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Time Slot</p>
                      <p className="font-semibold">
                        {selectedBooking.time_slot?.name}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Vehicle</p>
                    <p className="font-semibold">
                      {selectedBooking.vehicle?.make}{" "}
                      {selectedBooking.vehicle?.model}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedBooking.vehicle?.license_plate}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Floor</p>
                      <p className="font-semibold">{selectedBooking.floor}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Location</p>
                      <p className="font-semibold">
                        {selectedBooking.slot_number || "N/A"}
                      </p>
                    </div>
                  </div>

                  {selectedBooking.checked_in_at && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <p className="text-sm text-green-700 mb-1">Checked In</p>
                      <p className="font-semibold text-green-900">
                        {new Date(
                          selectedBooking.checked_in_at
                        ).toLocaleTimeString()}
                      </p>
                    </div>
                  )}

                  {selectedBooking.checked_out_at && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-700 mb-1">Checked Out</p>
                      <p className="font-semibold text-blue-900">
                        {new Date(
                          selectedBooking.checked_out_at
                        ).toLocaleTimeString()}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    {selectedBooking.status === "reserved" && (
                      <>
                        <Button
                          onClick={() => handleCheckIn(selectedBooking._id)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full"
                        >
                          <CheckCircle size={16} className="mr-2" />
                          Check In
                        </Button>
                        <Button
                          onClick={() => handleCancel(selectedBooking._id)}
                          variant="destructive"
                          className="flex-1 rounded-full"
                        >
                          <Trash2 size={16} className="mr-2" />
                          Cancel
                        </Button>
                      </>
                    )}
                    {selectedBooking.status === "checked_in" && (
                      <>
                        <Button
                          onClick={() => handleCheckOut(selectedBooking._id)}
                          className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-full"
                        >
                          <LogOut size={16} className="mr-2" />
                          Check Out
                        </Button>
                        <Button
                          onClick={() => handleCancel(selectedBooking._id)}
                          variant="destructive"
                          className="flex-1 rounded-full"
                        >
                          <Trash2 size={16} className="mr-2" />
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default TodaysBookingStatus;
